import { ROUTES } from '@/config/routes';

import type { CredentialState } from './credentialState';
import type { LinkedInConfigurationStatus } from './types';

/** How far a company has got through setting up its LinkedIn agent. */
export interface SetupProgress {
  /** The saved configuration's status, or null when none was saved. */
  configurationStatus: LinkedInConfigurationStatus | null;
  /** Whether the AI team was built. */
  hasTeam: boolean;
  /** Where the company's account stands. */
  credentialState: CredentialState;
  /** Whether a strategy was ever started: a plan exists, even one still being written. */
  hasStrategy: boolean;
}

/**
 * The setup step a person should be sent to, or **null** when the agent is fully set up and the
 * overview is where they belong.
 *
 * <p>The previous system's rule: configuration → team → account, whichever is the first not done.
 * Then one more: **the strategy**. It is started only by "Go to Dashboard" on the Review screen, so a person who
 * closed the page before pressing it has nothing built; they are sent to Review, not to an overview with no plan.
 *
 * <ul>
 *   <li>The configuration counts only when it is **`COMPLETE`**: all four steps explicitly saved. A
 *       `DRAFT` has autosaved fields without the wizard ever having been finished, so it is not done.</li>
 *   <li>An account counts when it is `connected` **or `stale`**: a session that has since died is a
 *       Reconnect on the overview, not a reason to run the whole setup again.</li>
 * </ul>
 */
export function resolveSetupRoute(progress: SetupProgress): string | null {
  if (progress.configurationStatus !== 'COMPLETE') return ROUTES.linkedinWelcome;
  if (!progress.hasTeam) return ROUTES.linkedinLaunch;
  if (progress.credentialState !== 'connected' && progress.credentialState !== 'stale') {
    return ROUTES.linkedinConnect;
  }
  if (!progress.hasStrategy) return ROUTES.linkedinReview;
  return null;
}
